(function () {
  'use strict';

  const { gsap } = window;
  const { BaseCarousel, ProductModelViewerManager } = window.Universe;

  class ProductMediaCarousel {
    constructor(section, options) {
      if (!section) {
        throw new Error('ProductMediaCarousel: section is required');
      }

      this.init(section, options);
    }

    setup() {
      // Height handling moved to CSS using aspect-ratio
      // auto: each item uses its own --aspect-ratio
      // adapt_first: all items use --first-aspect-ratio from container

      if (this.DOM.thumbsSlider) {
        const carouselOptions = {
          containScroll: 'trimSnaps',
          dragFree: true,
        };
        const isVertical = this.thumbsPosition === 'left';
        if (isVertical) carouselOptions.axis = 'y';
        this.thumbsInstance = new BaseCarousel({
          wrap: this.DOM.thumbsSlider,
          carouselOptions,
          onReady: () => {},
        });
      }

      this.mainInstance = new BaseCarousel({
        wrap: this.DOM.mainSlider,
        carouselOptions: this.mainCarouselOptions,
        withAutoHeight: this.mediaRatio === 'auto',
        onReady: () => {
          this.setupIndexes();
          this.updateIndicators();
          this.updateThumbs();
          this.setupVideoControls();
          this.setupVerticalExpansionScroll();
          this.setupModelViewers();
          this.setPosterImageAlt();
          this.setupSlideFocus();

          this.mainInstance.instance.on('select', this.onChangeSlide);

          for (let i = 0; i < this.DOM.thumbSlides.length; i++) {
            ((index) => {
              this.DOM.thumbSlides[index].addEventListener(
                'click',
                () => {
                  this.scrollInstances(index);
                },
                false,
              );
            })(i);
          }

          if (this.DOM.prev.length) {
            for (let i = 0; i < this.DOM.prev.length; i++) {
              this.DOM.prev[i].addEventListener('click', this.onPrev);
            }
          }

          if (this.DOM.next.length) {
            for (let i = 0; i < this.DOM.next.length; i++) {
              this.DOM.next[i].addEventListener('click', this.onNext);
            }
          }

          if (this.DOM.section) {
            this.DOM.section.addEventListener('keydown', this.onKeyDown);
          }

          this.updateLiveRegion(this.activeIndex);
        },
      });
    }

    setupSlideFocus() {
      const focusListenerOptions = { passive: true, capture: true };
      const instance = this.mainInstance.instance;
      const engineData = this.mainInstance.instance.internalEngine();
      const options = engineData.options;
      const eventStore = engineData.eventStore;

      function defaultCallback(index) {
        instance.rootNode().scrollLeft = 0;

        const selectedIndex = Math.floor(index / options.slidesToScroll);

        if (isNaN(selectedIndex)) return;

        instance.scrollTo(selectedIndex);
      }

      this.mainInstance.instance.slideNodes().forEach((slide, slideIndex) => {
        eventStore.add(
          slide,
          'focusin',
          (evt) => {
            defaultCallback(slideIndex);
          },
          focusListenerOptions,
        );
      });
    }

    setupModelViewers() {
      // Initialize model viewer manager for carousel
      if (this.DOM.section) {
        this.modelViewerManager = new ProductModelViewerManager(this.DOM.section, {
          onPlay: () => {
            if (this.mainInstance && this.mainInstance.instance) {
              this.mainInstance.instance.reInit({ watchDrag: false });
            }
          },
          onPause: () => {
            if (this.mainInstance && this.mainInstance.instance) {
              this.mainInstance.instance.reInit({ watchDrag: true });
            }
          },
        });
      }

      // Initialize model viewer manager for vertical expansion layout (desktop)
      const singleProduct = this.DOM.section.closest('.js-single-product');
      const section = this.DOM.section.closest('.single-product');

      if (singleProduct && section && section.dataset.layout === 'vertical_expansion') {
        const mediaList = section.querySelector('.product-media-list');
        if (mediaList) {
          this.verticalModelViewerManager = new ProductModelViewerManager(mediaList);
        }
      }
    }

    setPosterImageAlt(root = this.DOM.section) {
      if (!root) return;
      const posterImages = root.querySelectorAll('video img:not([alt])');
      posterImages.forEach((img) => {
        const mediaRoot = img.closest('[data-media-alt]');
        const altText = (mediaRoot && mediaRoot.dataset.mediaAlt) || '';
        img.setAttribute('alt', altText);
      });
    }

    setupVideoControls() {
      this.cleanupVideoControls();

      const videos = this.DOM.section.querySelectorAll('video');

      this.videoElements = videos;

      videos.forEach((video) => {
        const isLooping = video.loop;

        const onVideoEnded = () => {
          if (!isLooping) {
            video.controls = true;
          }
        };

        const onVideoPlay = () => {
          video.controls = false;
        };

        const onVideoClick = () => {
          if (video.controls) {
            video.play();
          }
        };

        video._videoEndedHandler = onVideoEnded;
        video._videoPlayHandler = onVideoPlay;
        video._videoClickHandler = onVideoClick;

        video.addEventListener('ended', onVideoEnded);
        video.addEventListener('play', onVideoPlay);
        video.addEventListener('click', onVideoClick);
      });
    }

    pauseEmbeddedVideos(index) {
      if (!this.DOM || !this.DOM.slides || !this.DOM.slides[index]) return;

      const slide = this.DOM.slides[index];

      const iframes = slide.querySelectorAll('iframe');
      iframes.forEach((iframe) => {
        const win = iframe.contentWindow;
        if (!win) return;

        const src = iframe.getAttribute('src') || '';
        if (src.indexOf('youtube') !== -1) {
          win.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } else if (src.indexOf('vimeo') !== -1) {
          win.postMessage('{"method":"pause"}', '*');
        }
      });
    }

    cleanupVideoControls() {
      if (this.videoElements) {
        this.videoElements.forEach((video) => {
          if (video._videoEndedHandler) {
            video.removeEventListener('ended', video._videoEndedHandler);
            delete video._videoEndedHandler;
          }
          if (video._videoPlayHandler) {
            video.removeEventListener('play', video._videoPlayHandler);
            delete video._videoPlayHandler;
          }
          if (video._videoClickHandler) {
            video.removeEventListener('click', video._videoClickHandler);
            delete video._videoClickHandler;
          }
        });
        this.videoElements = null;
      }
    }

    setupVerticalExpansionScroll() {
      if (window.innerWidth < 1024) return;

      const singleProduct = this.DOM.section.closest('.js-single-product');
      const section = this.DOM.section.closest('.single-product');

      if (!singleProduct || !section || section.dataset.layout !== 'vertical_expansion') {
        return;
      }

      let mediaContainer = this.DOM.section.querySelector('.product-media-list');

      if (!mediaContainer) {
        mediaContainer = this.DOM.section.querySelector('.product-media-carousel');
      }

      if (!mediaContainer) {
        return;
      }

      this.mediaContainer = mediaContainer;
      this.singleProduct = singleProduct;
      this.section = section;

      section.classList.remove('images-scrolled');

      const mediaContainerForScroll = section.querySelector('.js-product-media-list');
      if (mediaContainerForScroll) {
        mediaContainerForScroll.scrollTop = 0;
      }

      const onDocumentWheel = (e) => {
        const mediaZone = section.querySelector('.single-product__media-zone');
        if (!mediaZone) return;

        const mediaContainer = section.querySelector('.js-product-media-list');
        if (!mediaContainer) return;
        if (mediaContainer.scrollHeight <= mediaContainer.clientHeight) return;

        const isStickyMode = !section.classList.contains('images-scrolled');
        const scrollingDown = e.deltaY > 0;
        const scrollingUp = e.deltaY < 0;
        const isAtBottom = mediaContainer.scrollTop + mediaContainer.clientHeight >= mediaContainer.scrollHeight - 10;
        const isAtTop = mediaContainer.scrollTop <= 0;

        // Calculate new scroll destination (accumulate for multiple wheel events)
        if (typeof this._gsapScrollTarget !== 'number') this._gsapScrollTarget = mediaContainer.scrollTop;
        this._gsapScrollTarget += e.deltaY;
        this._gsapScrollTarget = Math.max(
          0,
          Math.min(this._gsapScrollTarget, mediaContainer.scrollHeight - mediaContainer.clientHeight),
        );

        // Determine whether to prevent default scroll
        let shouldPrevent = false;
        if (isStickyMode) {
          if ((scrollingDown && !isAtBottom) || (scrollingUp && !isAtTop)) {
            shouldPrevent = true;
          }
        } else {
          if (scrollingUp && window.scrollY <= 0) {
            shouldPrevent = true;
          } else if (
            scrollingDown &&
            mediaContainer.scrollTop < mediaContainer.scrollHeight - mediaContainer.clientHeight - 10
          ) {
            shouldPrevent = true;
          }
        }
        if (shouldPrevent) {
          e.preventDefault();
        } else {
          // If not blocking scroll, reset accumulated destination
          this._gsapScrollTarget = null;
          return;
        }

        // Cancel any previous animation
        if (this._gsapScrollTween) {
          this._gsapScrollTween.kill();
        }
        this._gsapScrollTween = gsap.to(mediaContainer, {
          scrollTop: this._gsapScrollTarget,
          duration: 0.28,
          ease: 'power1.out',
          overwrite: true,
          onUpdate: () => {
            const nowAtBottom =
              mediaContainer.scrollTop + mediaContainer.clientHeight >= mediaContainer.scrollHeight - 10;
            const nowAtTop = mediaContainer.scrollTop <= 0;
            if (isStickyMode) {
              if ((nowAtBottom && scrollingDown) || (nowAtTop && scrollingUp)) {
                section.classList.add('images-scrolled');
              }
            } else {
              if (scrollingUp && window.scrollY <= 0) {
                section.classList.remove('images-scrolled');
                if (mediaContainer.scrollTop <= 0) {
                  section.classList.add('images-scrolled');
                }
              } else if (
                scrollingDown &&
                mediaContainer.scrollTop + mediaContainer.clientHeight >= mediaContainer.scrollHeight - 10
              ) {
                section.classList.remove('images-scrolled');
                if (mediaContainer.scrollTop + mediaContainer.clientHeight >= mediaContainer.scrollHeight - 10) {
                  section.classList.add('images-scrolled');
                }
              }
            }
          },
        });
      };

      this._documentWheelHandler = onDocumentWheel;
      document.addEventListener('wheel', onDocumentWheel, { passive: false });
    }

    cleanupVerticalExpansionScroll() {
      if (this._documentWheelHandler) {
        document.removeEventListener('wheel', this._documentWheelHandler);
        delete this._documentWheelHandler;
      }

      if (this.section) {
        this.section.classList.remove('images-scrolled');
        this.section = null;
      }
    }

    scrollInstances(index) {
      if (this.mainInstance && this.mainInstance.instance) {
        this.mainInstance.instance.scrollTo(index);
      }
    }

    setupIndexes() {
      const indexes = Array.from(this.DOM.indexes).slice(1);
      indexes.forEach((index, i) => {
        gsap.set(index, { yPercent: 100 * (i + 1) });
      });
    }

    updateIndicators() {
      for (let i = 0; i < this.DOM.indicators.length; i++) {
        this.DOM.indicators[i].classList.toggle('is-active', i === this.activeIndex);
      }
    }

    updateThumbs() {
      for (let i = 0; i < this.DOM.thumbSlides.length; i++) {
        this.DOM.thumbSlides[i].classList.toggle('is-active', i === this.activeIndex);
      }
    }

    updateViewInSpace() {
      const activeMedia = this.mainInstance.instance.slideNodes()[this.activeIndex];

      if (activeMedia.getAttribute('data-media-type') === 'model') {
        this.DOM.viewInSpaceButton.setAttribute('data-shopify-model3d-id', activeMedia.getAttribute('data-index-id'));
      } else {
        this.DOM.viewInSpaceButton.setAttribute(
          'data-shopify-model3d-id',
          this.DOM.viewInSpaceButton.getAttribute('data-shopify-model3d-default-id'),
        );
      }
    }

    updateLiveRegion(index = this.activeIndex) {
      if (this.DOM.liveRegion) {
        const totalSlides = Math.max(this.slidesCount || this.DOM.slides.length || 0, 1);
        const currentIndex = Math.min(index + 1, totalSlides);
        const template = window.productMediaCarouselStrings?.mediaLabel || 'Media [index] of [total]';
        this.DOM.liveRegion.textContent = template.replace('[index]', currentIndex).replace('[total]', totalSlides);
      }
    }

    destroy() {
      if (this.DOM) {
        if (this.DOM.prev.length) {
          for (let i = 0; i < this.DOM.prev.length; i++) {
            this.DOM.prev[i].removeEventListener('click', this.onPrev);
          }
        }

        if (this.DOM.next.length) {
          for (let i = 0; i < this.DOM.next.length; i++) {
            this.DOM.next[i].removeEventListener('click', this.onNext);
          }
        }

        this.cleanupVideoControls();

        if (this.DOM.section) {
          this.DOM.section.removeEventListener('keydown', this.onKeyDown);
        }

        if (this.modelViewerManager) {
          this.modelViewerManager.destroy();
          this.modelViewerManager = null;
        }

        if (this.verticalModelViewerManager) {
          this.verticalModelViewerManager.destroy();
          this.verticalModelViewerManager = null;
        }

        // Remove vertical expansion scroll event listeners
        this.cleanupVerticalExpansionScroll();

        if (this.mainInstance && this.mainInstance.destroy) {
          this.mainInstance.destroy();
        }
        if (this.thumbsInstance && this.thumbsInstance.destroy) {
          this.thumbsInstance.destroy();
        }

        this.mainInstance = undefined;
        this.thumbsInstance = undefined;
        this.DOM = undefined;
      }
    }

    init(section, options) {
      if (section) {
        this.DOM = {};
        this.DOM.section = section;
        this.DOM.mainSlider = this.DOM.section.querySelector('.js-product-media-carousel-main');
        this.DOM.indexes = this.DOM.section.querySelectorAll('.js-product-media-carousel-index');
        this.DOM.indicators = this.DOM.section.querySelectorAll('.js-product-media-carousel-indicator');
        this.DOM.prev = this.DOM.section.querySelectorAll('.js-carousel-prev');
        this.DOM.next = this.DOM.section.querySelectorAll('.js-carousel-next');
        this.DOM.viewInSpaceButton = this.DOM.section.querySelector('[data-shopify-xr]');

        this.DOM.slides = this.DOM.section.querySelectorAll('.js-product-media-carousel-slide');
        this.DOM.thumbsSlider = this.DOM.section.querySelector('.js-product-thumbs-carousel');
        this.DOM.thumbSlides = this.DOM.section.querySelectorAll('.js-product-thumbs-slide');
        this.DOM.liveRegion = this.DOM.section.querySelector('[data-media-live-region]');

        this.mainCarouselOptions = options || { containScroll: 'trimSnaps', watchDrag: true, watchFocus: false };
        this.mediaRatio = this.DOM.section.dataset.mediaRatio;
        this.thumbsPosition = this.DOM.section.dataset.thumbsPosition;

        this.mainInstance = [];
        this.activeIndex = 0;
        this.slidesCount = this.DOM.slides.length;

        this.onChangeSlide = () => {
          const nextIndex = this.mainInstance.instance.selectedScrollSnap();
          gsap.killTweensOf(this.DOM.indexes);
          gsap.to(this.DOM.indexes, {
            duration: 0.4,
            yPercent: (i) => {
              return 100 * (i - nextIndex);
            },
          });

          if (this.thumbsInstance && this.thumbsInstance.instance) {
            this.thumbsInstance.instance.scrollTo(nextIndex);
          }

          this.pauseEmbeddedVideos(this.activeIndex);

          this.activeIndex = nextIndex;

          this.updateIndicators();
          this.updateThumbs();

          if (this.modelViewerManager) {
            this.modelViewerManager.pauseAll();
          }

          if (this.DOM.viewInSpaceButton) {
            this.updateViewInSpace();
          }

          this.updateLiveRegion();
        };

        this.onKeyDown = (event) => {
          if (!this.mainInstance || !this.mainInstance.instance) return;
          if (event.defaultPrevented) return;
          if (event.altKey || event.ctrlKey || event.metaKey) return;

          const targetTag = event.target && event.target.tagName ? event.target.tagName.toLowerCase() : '';
          if (targetTag === 'input' || targetTag === 'textarea' || targetTag === 'select') return;

          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.mainInstance.instance.scrollPrev();
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.mainInstance.instance.scrollNext();
          }
        };

        this.onPrev = (event) => {
          this.mainInstance.instance.scrollPrev();
        };

        this.onNext = (event) => {
          this.mainInstance.instance.scrollNext();
        };

        this.setup();
      }
    }
  }

  window.Universe.ProductMediaCarousel = ProductMediaCarousel;
})();
